import * as XLSX from 'xlsx';
import { format, parseISO } from 'date-fns';
import type { ReportUser, AttendanceEntry } from '../types/report';

/** Prefer the OSRM-corrected total when the backend has one, otherwise
 *  fall through to the raw haversine distance. Matches the on-screen
 *  Report Grid rule so exports and the UI agree byte-for-byte. */
function effectiveMeters(entry: AttendanceEntry): number {
  return entry.total_modified_distance_meters ?? entry.total_distance_meters ?? 0;
}

/** Same significance test the Report Grid uses to decide whether a day
 *  gets the "corrected" checkmark — anything smaller is float noise. */
function isCorrected(entry: AttendanceEntry): boolean {
  const modified = entry.total_modified_distance_meters;
  if (modified === null || modified === undefined) return false;
  const raw = entry.total_distance_meters ?? 0;
  return Math.abs(modified - raw) >= 1;
}

/** Format seconds into "Xh Ym" */
function formatDuration(seconds: number): string {
  if (!seconds || seconds <= 0) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}

/** Format meters into "X.X km" or "X m" */
function formatMeters(meters: number): string {
  if (!meters || meters <= 0) return '—';
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(1)} km`;
}

/** Format an ISO time string to HH:mm */
function fmtTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return format(parseISO(iso), 'HH:mm');
  } catch {
    return '—';
  }
}

/** Coerce any value to a safe string */
function toStr(val: unknown): string {
  if (val == null) return '';
  if (Array.isArray(val)) return val.join(', ');
  return String(val);
}

/**
 * Sanitize a cell value to prevent Excel formula injection.
 * Prefixes with an apostrophe if the value starts with =, +, -, @, or tab.
 */
function sanitize(val: unknown): string {
  const s = toStr(val);
  if (!s) return '';
  const first = s.charAt(0);
  if (first === '=' || first === '+' || first === '-' || first === '@' || first === '\t') {
    return `'${s}`;
  }
  return s;
}

/** Build an attendance lookup map for a user: date string → entry */
function buildAttendanceMap(attendance: AttendanceEntry[] | undefined | null): Map<string, AttendanceEntry> {
  const map = new Map<string, AttendanceEntry>();
  if (!attendance) return map;
  for (const entry of attendance) {
    map.set(entry.date, entry);
  }
  return map;
}

/**
 * Flatten report data into headers + rows for export.
 * Each date gets one column with multi-line cell content.
 */
export function flattenReportData(
  users: ReportUser[],
  dates: Date[]
): { headers: string[]; rows: string[][] } {
  const dateStrings = dates.map((d) => format(d, 'yyyy-MM-dd'));
  const dateLabels = dates.map((d) => format(d, 'EEE dd MMM'));

  const headers = [
    'Name',
    'Employee ID',
    'Role',
    'Program',
    ...dateLabels,
    'Days Present',
    'Total Distance',
    'Total Hours',
  ];

  const rows: string[][] = users.map((user) => {
    const aMap = buildAttendanceMap(user.attendance);
    let totalDays = 0;
    let totalMeters = 0;
    let totalSeconds = 0;

    const dateCells = dateStrings.map((ds) => {
      const entry = aMap.get(ds);
      if (!entry) return 'Absent';

      totalDays++;
      totalMeters += effectiveMeters(entry);
      totalSeconds += entry.total_time_active_seconds ?? 0;

      const inTime = fmtTime(entry.start_time);
      const outTime = fmtTime(entry.end_time);
      const dur = formatDuration(entry.total_time_active_seconds);
      // Use the effective (modified) distance so the export mirrors the
      // grid; tag corrected days so an auditor can see which totals were
      // adjusted by an OSRM correction.
      const dist = formatMeters(effectiveMeters(entry));
      const suffix = isCorrected(entry) ? ' (corrected)' : '';
      return `In: ${inTime} | Out: ${outTime} | ${dur} | ${dist}${suffix}`;
    });

    return [
      sanitize(user.name),
      sanitize(user.employee_id),
      sanitize(user.role),
      sanitize(user.program_id),
      ...dateCells,
      `${totalDays}/${dates.length}`,
      formatMeters(totalMeters),
      formatDuration(totalSeconds),
    ];
  });

  return { headers, rows };
}

/** Escape a CSV cell value */
function escapeCSV(val: string): string {
  const s = val ?? '';
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

/** Download a CSV file from headers + rows */
export function exportToCSV(
  filename: string,
  headers: string[],
  rows: string[][]
) {
  const csvLines = [
    headers.map(escapeCSV).join(','),
    ...rows.map((row) => row.map(escapeCSV).join(',')),
  ];

  const csvString = csvLines.join('\n');
  const blob = new Blob(['\uFEFF' + csvString], { type: 'text/csv;charset=utf-8;' });
  triggerDownload(blob, filename);
}

/** Download an Excel file from headers + rows */
export function exportToExcel(
  filename: string,
  headers: string[],
  rows: string[][]
) {
  const aoa = [headers, ...rows];
  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Auto-size columns based on max content length
  ws['!cols'] = headers.map((h, i) => {
    const maxLen = Math.max(
      h.length,
      ...rows.map((r) => (r[i] || '').length)
    );
    return { wch: Math.min(Math.max(maxLen + 2, 12), 40) };
  });

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Attendance Report');
  XLSX.writeFile(wb, filename);
}

/** Trigger browser download from a Blob */
function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
